import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';

/**
 * Rate limit configuration
 */
export const RATE_LIMITS = {
  createChat: { maxRequests: 5, windowMs: 60000 }, // 5 chats per minute
  sendMessage: { maxRequests: 30, windowMs: 60000 }, // 30 messages per minute
  createGroup: { maxRequests: 3, windowMs: 3600000 }, // 3 groups per hour
  createEvent: { maxRequests: 5, windowMs: 3600000 }, // 5 events per hour
  vote: { maxRequests: 20, windowMs: 60000 }, // 20 votes per minute
};

/**
 * Check if user has exceeded rate limit
 * @param {string} userId - User ID
 * @param {string} action - Action type
 * @returns {Promise<boolean>} True if allowed, false if rate limited
 */
export const checkRateLimit = async (userId, action) => {
  try {
    const config = RATE_LIMITS[action];
    if (!config) {
      console.warn(`No rate limit config for action: ${action}`);
      return true; // Allow if no config
    }

    const rateLimitRef = doc(db, 'rateLimits', `${userId}_${action}`);
    const rateLimitDoc = await getDoc(rateLimitRef);

    const now = Date.now();
    const windowStart = now - config.windowMs;

    if (!rateLimitDoc.exists()) {
      // First request - create document
      await setDoc(rateLimitRef, {
        userId,
        action,
        count: 1,
        windowStart: now,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return true;
    }

    const data = rateLimitDoc.data();

    // Reset if window expired
    if (data.windowStart < windowStart) {
      await updateDoc(rateLimitRef, {
        count: 1,
        windowStart: now,
        updatedAt: serverTimestamp()
      });
      return true;
    }

    // Check limit
    if (data.count >= config.maxRequests) {
      console.warn(`Rate limit exceeded for user ${userId} on action ${action}`);
      return false;
    }

    // Increment count
    await updateDoc(rateLimitRef, {
      count: data.count + 1,
      updatedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error('Error checking rate limit:', error);
    // Fail open - allow request if rate limiting fails
    return true;
  }
};

/**
 * Get rate limit status for user
 * @param {string} userId - User ID
 * @param {string} action - Action type
 * @returns {Promise<object>} Rate limit status
 */
export const getRateLimitStatus = async (userId, action) => {
  try {
    const rateLimitRef = doc(db, 'rateLimits', `${userId}_${action}`);
    const rateLimitDoc = await getDoc(rateLimitRef);

    if (!rateLimitDoc.exists()) {
      return {
        remaining: RATE_LIMITS[action]?.maxRequests || 0,
        resetAt: Date.now() + (RATE_LIMITS[action]?.windowMs || 60000)
      };
    }

    const data = rateLimitDoc.data();
    const config = RATE_LIMITS[action];
    const now = Date.now();
    const windowStart = now - config.windowMs;

    // Reset if window expired
    if (data.windowStart < windowStart) {
      return {
        remaining: config.maxRequests,
        resetAt: now + config.windowMs
      };
    }

    return {
      remaining: Math.max(0, config.maxRequests - data.count),
      resetAt: data.windowStart + config.windowMs
    };
  } catch (error) {
    console.error('Error getting rate limit status:', error);
    return {
      remaining: 0,
      resetAt: Date.now() + 60000
    };
  }
};
